import { ComponentProps, useEffect } from 'react';

import { OrdersModel, SyncBillModel } from '@pukka/api';
import { Badge } from '@pukka/ui-components';
import { endOfDay, format, isValid, startOfDay } from 'date-fns';
import noop from 'lodash/noop';
import { subscribeKey } from 'valtio/utils';

import { TAB, TransactionState } from './Transaction.state';

function getExportTitle(tab: TAB) {
  switch (tab) {
    case 'cancelled':
      return 'Cancelled Orders';
    case 'void':
      return 'Void Items';
    case 'success':
    default:
      return 'Transaction';
  }
}

function getSearchValue(tab: TAB, receipt: string, itemName: string, orderId: string) {
  switch (tab) {
    case 'cancelled':
      return orderId;
    case 'void':
      return itemName;
    case 'success':
    default:
      return receipt;
  }
}

function getLoadingValue(tab: TAB, cancelledStatus: boolean, voidStatus: boolean) {
  switch (tab) {
    case 'cancelled':
      return cancelledStatus;
    case 'void':
      return voidStatus;
    case 'success':
    default:
      return false;
  }
}

function getSearchPlaceholder(tab: TAB) {
  switch (tab) {
    case 'cancelled':
      return 'Order ID';
    case 'void':
      return 'Item Name';
    case 'success':
    default:
      return 'Receipt Number';
  }
}

function getBadgeColor(tab: TAB): ComponentProps<typeof Badge>['color'] {
  switch (tab) {
    case 'cancelled':
      return 'red';
    case 'void':
      return 'neutral';
    case 'success':
    default:
      return 'green';
  }
}

function getBadgeText(tab: TAB) {
  switch (tab) {
    case 'cancelled':
      return 'Cancelled';
    case 'void':
      return 'Void';
    case 'success':
    default:
      return 'Completed';
  }
}

function useInitialData(State: typeof TransactionState, onChange: () => void) {
  useEffect(() => {
    if (State.outletById.size === 0) {
      const unsubscribe = subscribeKey(State, 'outletById', () => {
        onChange();
        unsubscribe();
      });

      return unsubscribe;
    }

    onChange();

    return noop;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

function getNowInsteadNull(date: Date, snapTo?: 'start' | 'end', formatString = "yyyy-MM-dd'T'HH:mm:ssxxx") {
  const now = snapTo === 'start' ? startOfDay(new Date()) : endOfDay(new Date());

  return format(isValid(date) ? date : now, formatString);
}

function getItemNameWithBundleComponent(item: OrdersModel.OrderItem | SyncBillModel.Item) {
  if ((item as OrdersModel.OrderItem).item_name) {
    const bundleItems = (item as OrdersModel.OrderItem).bundle_components?.map((bc) => `${bc.quantity}x ${bc.name}`).join('\n');

    return `${(item as OrdersModel.OrderItem).item_name}${bundleItems ? `:\n${bundleItems}` : ''}`;
  }

  if ((item as SyncBillModel.Item).itemName) {
    const bundleItems = (item as SyncBillModel.Item).bundleComponents?.map((bc) => `${bc.itemCount}x ${bc.itemName}`).join('\n');

    return `${(item as SyncBillModel.Item).itemName}${bundleItems ? `:\n${bundleItems}` : ''}`;
  }

  return '';
}

export {
  getExportTitle,
  getSearchValue,
  getSearchPlaceholder,
  getBadgeColor,
  getBadgeText,
  useInitialData,
  getNowInsteadNull,
  getItemNameWithBundleComponent,
  getLoadingValue,
};
